import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    deleteV2ProviderDocumentReferencesByDocumentReferenceId: build.mutation<
      DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse,
      DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/document-references/${queryArg.documentReferenceId}`,
        method: "DELETE",
      }),
    }),
    getV2ProviderDocumentReferencesByDocumentReferenceId: build.query<
      GetV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse,
      GetV2ProviderDocumentReferencesByDocumentReferenceIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/document-references/${queryArg.documentReferenceId}`,
      }),
    }),
    getV2ProviderPatientsByPatientIdDocumentReferences: build.query<
      GetV2ProviderPatientsByPatientIdDocumentReferencesApiResponse,
      GetV2ProviderPatientsByPatientIdDocumentReferencesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/document-references`,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
          doc_kind: queryArg.docKind,
          sensitivity_level: queryArg.sensitivityLevel,
          encounter_id: queryArg.encounterId,
          source_artifact_id: queryArg.sourceArtifactId,
          date_from: queryArg.dateFrom,
          date_to: queryArg.dateTo,
          q: queryArg.q,
          sort: queryArg.sort,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse =
  unknown;
export type DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiArg = {
  documentReferenceId: string;
};
export type GetV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse =
  /** status 200 OK */ DocumentReferenceDetailResponse;
export type GetV2ProviderDocumentReferencesByDocumentReferenceIdApiArg = {
  documentReferenceId: string;
};
export type GetV2ProviderPatientsByPatientIdDocumentReferencesApiResponse =
  /** status 200 OK */ DocumentReferenceListResponse;
export type GetV2ProviderPatientsByPatientIdDocumentReferencesApiArg = {
  patientId: string;
  /** Max results per page */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
  /** Filter by one or more doc_kinds (clinical_note, lab_report, imaging, intake_form, consent, roi, insurance_card, referral, prior_records_packet, legal_document, discharge_summary, neuropsych_eval, assessment, markdown_doc, form, other). Repeat the param for OR semantics. */
  docKind?: string[] | null;
  /** Filter by sensitivity level (unrestricted | phi_standard | sud_treatment | psychotherapy_note). */
  sensitivityLevel?: string[] | null;
  /** Restrict to records threaded under a specific encounter. */
  encounterId?: string | null;
  /** Restrict to records extracted from a specific artifact. */
  sourceArtifactId?: string | null;
  /** Inclusive lower bound on effective_date (authored_at when present, else created_at). */
  dateFrom?: string | null;
  /** Inclusive upper bound on effective_date. */
  dateTo?: string | null;
  /** Full-text search across title + description. Supports phrase quoting and boolean operators (Postgres websearch_to_tsquery). */
  q?: string | null;
  /** Ordering:
   * timeline (default) — authored_at when set, else created_at (the chart's natural chronological view).
   * uploaded_at_desc — purely created_at.
   * authored_at_desc — only authored_at, nulls last. */
  sort?: "timeline" | "uploaded_at_desc" | "authored_at_desc";
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type DocRefProcessingInfo = {
  error?: string | null;
  extracted_at?: string | null;
  stages_completed?: string[];
};
export type DocRefSectionBBox = {
  h: number;
  page: number;
  w: number;
  x: number;
  y: number;
};
export type DocRefSection = {
  bboxes?: DocRefSectionBBox[];
  page_end?: number | null;
  page_start?: number | null;
  retrieval_hint?: string | null;
  section_id: string;
  summary?: string | null;
  tags?: string[];
  title: string;
  token_estimate?: number | null;
};
export type DocumentReferenceMetadata = {
  answerable_questions?: string[];
  author_facility?: string | null;
  author_provider_name?: string | null;
  extra?: {
    [key: string]: any;
  };
  extractions?: {
    [key: string]: {
      [key: string]: any;
    };
  };
  language?: string;
  processing?: DocRefProcessingInfo;
  sections?: DocRefSection[];
  tags?: string[];
};
export type DocumentReferenceDetailResponse = {
  author_facility?: string | null;
  author_provider_name?: string | null;
  authored_at?: string | null;
  created_at: string;
  derived_via?: string | null;
  description?: string | null;
  doc_kind?: string | null;
  document_reference_id: string;
  effective_date?: string | null;
  encounter_id?: string | null;
  metadata?: DocumentReferenceMetadata;
  organization_id: string;
  patient_id?: string | null;
  processing_status: string;
  scope: string;
  sensitivity_level?: string | null;
  signed_at?: string | null;
  source_artifact_id?: string | null;
  source_location?: {
    [key: string]: any;
  } | null;
  supersedes_document_reference_id?: string | null;
  tags?: string[];
  title?: string | null;
  updated_at: string;
};
export type DocumentReferenceSummaryResponse = {
  author_facility?: string | null;
  author_provider_name?: string | null;
  authored_at?: string | null;
  created_at: string;
  derived_via?: string | null;
  description?: string | null;
  doc_kind?: string | null;
  document_reference_id: string;
  effective_date?: string | null;
  encounter_id?: string | null;
  organization_id: string;
  patient_id?: string | null;
  processing_status: string;
  scope: string;
  sensitivity_level?: string | null;
  signed_at?: string | null;
  source_artifact_id?: string | null;
  source_location?: {
    [key: string]: any;
  } | null;
  supersedes_document_reference_id?: string | null;
  tags?: string[];
  title?: string | null;
  updated_at: string;
};
export type DocumentReferenceListResponse = {
  items: DocumentReferenceSummaryResponse[];
  limit: number;
  offset: number;
  total: number;
};
